import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment-timezone";

import {
  DefaultImageBaseurl,
  PlayerSiteUrl,
  endPoints,
} from "../../../constant/Environment";
import { addData, deleteData, getAllData } from "../../../Utility/API";
import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import Toaster from "../../../components/Toast";
import Loader from "../../../components/Loader";
import DeleteModel from "../../../components/DeleteModel";
import { ErrorToast, SuccesToast } from "../../../components/Toast/message";
import { tokens } from "../../../theme";
import { mono, th, td, TablePaginationActions } from "../../../components/tableKit";
import { uploadHeaders } from "../../../Utility/axiosInstances";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Panel treatment used across src/pages. Flat surface rather than the card
// gradient, so the form fields sit on an even background top to bottom.
const dialogPaperSx = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: "12px",
  backgroundImage: "none",
};

// The legacy MUI-table screens (e.g. /games) are sized by skin.css at 11px
// headers / 13.5px body. tableKit's defaults are a step smaller, so bump them
// here to keep this screen visually consistent with those.
const thCell = { ...th, fontSize: 11, letterSpacing: 0.6 };
const tdCell = { ...td, fontSize: 13.5 };

const fieldSx = { "& .MuiInputBase-root": { fontSize: 13 } };

const fmtAuditDate = (d) =>
  d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD HH:mm:ss") : "—";

const ghostBtnSx = {
  textTransform: "none",
  fontSize: 12.5,
  color: tokens.textDim,
  borderColor: tokens.border,
  "&:hover": { borderColor: tokens.border2 },
};

// exclude_player normally comes back as an array of player_type ids, but older
// records answer with a comma string or null — normalise before comparing.
const excludedPlayerIds = (row) => {
  const raw = row?.exclude_player;
  if (Array.isArray(raw)) return raw.map(Number);
  if (typeof raw === "string" && raw.length > 0) return raw.split(",").map(Number);
  return [];
};

const LIMIT_OPTIONS = [
  ...Array.from({ length: 30 }, (_, index) => index + 1),
  100,
  200,
  300,
  400,
  500,
];

function DialogHeader({ title, onClose }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Typography variant="h6" sx={{ fontSize: 16 }}>
        {title}
      </Typography>
      <IconButton size="small" onClick={onClose} aria-label="close">
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// Label above a control, matching the uppercase micro-labels used on the
// redesigned pages.
function Field({ label, children, span = 6 }) {
  return (
    <Box sx={{ gridColumn: { xs: "span 12", md: `span ${span}` } }}>
      <Typography
        sx={{
          color: tokens.textDim,
          fontSize:12,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 700,
          mb: 0.6,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

const CoinPackage = () => {
  const [editData, setEditData] = useState({});
  const [createData, setCreateData] = useState(false);
  const [rowDataToDelete, setRowDataToDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [packageName, setPackageName] = useState("");
  const [goldCoin, setGoldCoin] = useState("");
  const [sagaCoin, setSagaCoin] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [maxCliam, setMaxCliam] = useState(0);
  const [menuSort, setMenuSort] = useState("");

  const [dailyBounsAmount, setDailyBounsAmount] = useState("");
  const [bounsDays, setBounsDays] = useState("");
  // Submitted with the package; there is no input for it in the form today.
  const [bonusCoin, setBonusCoin] = useState("");

  const [creative, setCreative] = useState("");
  const [showcreative, setShowCreative] = useState("");
  const [creativeName, setCreativeName] = useState("");

  const [day, setDay] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [limit, setLimit] = useState("");

  const [pending, setPending] = useState(true);
  const [show, setShow] = useState(false);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = () => setShow(false);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [totalPackage, setTotalPackage] = useState([]);
  const [excludePackage, setExcludePackage] = useState([]);
  const [sortOrder, setSortOrder] = useState("");

  const [playerList, setPlayerList] = useState([]);
  const [excludePlayerTypes, setExcludePlayerTypes] = useState([]);

  const handleChange = (event) => {
    const value = event.target.value;
    setExcludePackage(typeof value === "string" ? value.split(",") : value);
  };

  const [checkboxes, setCheckboxes] = useState({
    isCheckedActive: false,
    isFeature: false,
    isPopular: false,
    isSpecial: false,
    dailyBounsEnable: false,
    freeSpin: false,
    isBoost: false,
    dailyStreak: false,
    emailPackage: false,
    isLottery: false,
  });

  const [freeSpinValue, setFreeSpinValue] = useState("");
  const [freeSpinGameName, setFreeSpinGameName] = useState("");
  const [freeSpinGame, setFreeSpinGame] = useState(null);
  const [freespingameid, setFreeSpingameid] = useState("");
  const [freeSpinFromDate, setFreeSpinFromDate] = useState("");
  const [freeSpinToDate, setFreeSpinToDate] = useState("");
  const [boostValue, setBoostValue] = useState("");
  const [featureName, setFeatureName] = useState("");
  const [gameSearchTerm, setGameSearchTerm] = useState("");
  const [filteredGames, setFilteredGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [spincount, setSpincount] = useState("");
  const [copyUrlVisible, setCopyUrlVisible] = useState(false);
  const [packageUrl, setPackageUrl] = useState("");

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditPackage, setAuditPackage] = useState(null);

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setCheckboxes((prevCheckboxes) => ({
      ...prevCheckboxes,
      [name]: checked,
    }));
    if (name === "isBoost" && !checked) {
      setBoostValue("");
    }
    if (name === "emailPackage") {
      if (checked && editData?.id) {
        const url = fetchEmailurl(editData);
        setPackageUrl(url);
        setCopyUrlVisible(!!url);
      } else {
        setCopyUrlVisible(false);
        setPackageUrl("");
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setCreative(file);
      setCreativeName(file.name);
      setShowCreative(imageUrl);
      UploadImage(file);
    }
  };

  const handleCopyUrl = () => {
    if (packageUrl) {
      navigator.clipboard
        .writeText(packageUrl)
        .then(() => {
          SuccesToast("URL copied to clipboard!");
        })
        .catch((error) => {
          console.error("Failed to copy:", error);
          ErrorToast("Failed to copy URL");
        });
    }
  };

  const UploadImage = (files) => {
    var formdata = new FormData();
    formdata.append("file", files);
    var requestOptions = {
      method: "POST",
      body: formdata,
      redirect: "follow",
      headers: uploadHeaders(),
    };
    setIsLoading(true);
    let imgUrl = `${endPoints.apiBaseUrl}${endPoints.api.UPLOAD_BANNER}`;
    fetch(imgUrl, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        setCreative(result.data);
        setIsLoading(false);
      })
      .catch((error) => console.log("error", error));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchCoinPackage();
    getPlayerTypes();
  }, []);

  const getPlayerTypes = () => {
    let url = `${endPoints.api.playertypes}`;
    getAllData(url)
      .then((response) => {
        if (response.status === "success") {
          setPlayerList(response.data);
        }
      })
      .catch((err) => console.error("Error fetching player types:", err));
  };

  const searchGames = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setFilteredGames([]);
      return;
    }

    setLoadingGames(true);
    try {
      let url_gamesearch = `${endPoints.api.SEARCH_FREESPIN_GAMES}/${encodeURIComponent(
        searchTerm
      )}`;
      const response = await getAllData(url_gamesearch);

      if (response?.status === "success") {
        setFilteredGames(response?.data || []);
      } else {
        console.error("Failed to search games:", response);
        setFilteredGames([]);
      }
    } catch (error) {
      console.error("Error searching games:", error);
      setFilteredGames([]);
    } finally {
      setLoadingGames(false);
    }
  };

  useEffect(() => {
    const delayedGameSearch = setTimeout(() => {
      if (gameSearchTerm) {
        searchGames(gameSearchTerm);
      } else {
        setFilteredGames([]);
      }
    }, 500);

    return () => clearTimeout(delayedGameSearch);
  }, [gameSearchTerm]);

  const fetchCoinPackage = async () => {
    let url = `${endPoints.api.GET_PACKAGE}`;
    setIsLoading(true);
    await getAllData(url)
      .then((response) => {
        setIsLoading(false);
        if (response.status === "success") {
          setRows(response.data);
          setTotalPackage(response.data);
          setPending(false);
        } else {
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const handleOk = () => {
    if (checkboxes.freeSpin) {
      if (spincount === "" || freeSpinValue === "" || freespingameid === "") {
        ErrorToast("Please select all free spin values to enable free spin");
        return;
      }
    }
    if (
      packageName &&
      // goldCoin &&
      // sagaCoin &&
      purchasePrice &&
      maxCliam &&
      // menuSort &&
      creative
    ) {
      if (checkboxes.isBoost && !boostValue) {
        ErrorToast("Please add boost value when Coin Boost is enabled");
        return;
      }
      let url = editData?.id
        ? `${endPoints.api.UPDATE_PACKAGE}`
        : `${endPoints.api.ADD_PACKAGE}`;

      let payload = {
        package_name: packageName,
        creative: creative,
        goldcoins: goldCoin,
        sagacoins: sagaCoin,
        purchase_price: purchasePrice,
        max_claim_perday: maxCliam,
        menu_sort_order: menuSort,
        isactive: checkboxes.isCheckedActive ? 1 : 0,
        max_claim: limit,
        days: day,
        isFeature: checkboxes.isFeature ? 1 : 0,
        isPopular: checkboxes.isPopular ? 1 : 0,
        exclude_package: excludePackage,
        exclude_player: excludePlayerTypes,
        isSpecial: checkboxes.isSpecial ? 1 : 0,
        daily_bouns_enable: checkboxes.dailyBounsEnable ? 1 : 0,
        isBoost: checkboxes.isBoost ? 1 : 0,
        daily_bouns_amount: dailyBounsAmount,
        bouns_days: bounsDays,
        multplier: multiplier,
        freeSpin: checkboxes.freeSpin ? true : false,
        spinCount: spincount,
        spinValue: freeSpinValue,
        featureName: featureName,
        spinStartDate: freeSpinFromDate,
        spinEndDate: freeSpinToDate,
        gameName: gameSearchTerm || "",
        gameId: freespingameid || "",
        sortOrder: sortOrder || "",
        boostValue: checkboxes.isBoost ? boostValue : "",
        dailyStreak: checkboxes.dailyStreak ? 1 : 0,
        isLottery: checkboxes.isLottery ? 1 : 0,
        emailPackage: checkboxes.emailPackage ? 1 : 0,
        bonus_amount: bonusCoin,
      };
      if (editData?.id) {
        payload.id = editData.id;
      }
      setIsLoading(true);
      addData(url, payload)
        .then((response) => {
          setIsLoading(false);
          if (response.data.status === "success") {
            SuccesToast(response?.data?.message);
            fetchCoinPackage();

            handleClose();
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.error(error);
          ErrorToast(error);
        });
    } else {
      ErrorToast("all fields are required");
    }
  };

  useEffect(() => {
    if (editData) {
      // Served from the CDN, same as the creative thumbnails in the table.
      let imageFullPath = editData.creative
        ? `${DefaultImageBaseurl}${editData.creative}`
        : "";
      setPackageName(editData.package_name || "");
      setCreative(editData.creative || "");
      setCreativeName("");
      setShowCreative(imageFullPath || "");
      setGoldCoin(editData.goldcoins || "");
      setSagaCoin(editData.sagacoins || "");
      setPurchasePrice(editData.purchase_price || "");
      setMaxCliam(editData.max_claim_perday || "");
      setMenuSort(editData.menu_sort_order || "");
      setSortOrder(editData.sortOrder || "");
      setCheckboxes((prevCheckboxes) => ({
        ...prevCheckboxes,
        isCheckedActive: editData.isactive || false,
        isFeature: editData.isFeature || false,
        isPopular: editData.isPopular || false,
        isSpecial: editData?.isSpecial || false,
        dailyBounsEnable: editData?.daily_bouns_enable || false,
        freeSpin: editData?.freeSpin || false,
        isBoost: editData?.isBoost ? true : false,
        dailyStreak: editData?.dailyStreak ? true : false,
        emailPackage: editData?.emailPackage ? true : false,
        isLottery: editData?.isLottery ? true : false,
      }));
      setBounsDays(editData?.bouns_days || "");
      setDailyBounsAmount(editData?.daily_bouns_amount || "");
      setBonusCoin(editData?.bonus_amount || "");

      setLimit(editData.max_claim || "");
      setDay(editData.days || "");
      setMultiplier(editData?.multplier || "");
      setExcludePackage(editData?.exclude_package || []);
      setExcludePlayerTypes(editData?.exclude_player || []);
      setFreeSpinValue(editData?.spinValue || "");
      setSpincount(editData?.spinCount || "");
      setFeatureName(editData?.featureName || "");
      setFreeSpinFromDate(
        editData?.spinStartDate
          ? moment(editData.spinStartDate).format("YYYY-MM-DD")
          : ""
      );
      setFreeSpinToDate(
        editData?.spinEndDate ? moment(editData.spinEndDate).format("YYYY-MM-DD") : ""
      );
      setGameSearchTerm(editData?.gameName || "");
      setFreeSpingameid(editData?.gameId || "");
      setBoostValue(editData?.boostValue || "");
      // Fetch game details to populate freeSpinGame with freeCounts and betSize
      if (editData?.gameId) {
        Gamedetail(editData.gameId).then((gameData) => {
          if (gameData) {
            setFreeSpinGame(gameData);
          }
        });
      }
      // Set copy URL if email package is enabled and ID exists
      if (editData?.emailPackage && editData?.id) {
        const url = fetchEmailurl(editData);
        setPackageUrl(url);
        setCopyUrlVisible(!!url);
      } else {
        setCopyUrlVisible(false);
        setPackageUrl("");
      }
    }
  }, [editData]);

  const Gamedetail = async (gameid) => {
    let url = `${endPoints.api.GAME_DETAILS}/${gameid}`;
    try {
      const response = await getAllData(url);
      if (response?.status === "success") {
        return response?.data;
      }
    } catch (error) {
      console.error("Error fetching game details:", error);
      return null;
    }
  };

  const handleDelete = (row) => {
    setRowDataToDelete(row);
    setShowDeleteModal(true);
  };

  const openAudit = (row) => {
    setAuditPackage(row);
    setAuditOpen(true);
    fetchAudit(row.id);
  };

  const closeAudit = () => {
    setAuditOpen(false);
    setAuditPackage(null);
    setAuditRows([]);
  };

  const fetchAudit = async (id) => {
    setAuditLoading(true);
    try {
      const response = await getAllData(`${endPoints.api.GET_PACKAGE_AUDIT}/${id}`);
      if (response?.status === "success") {
        setAuditRows(response.data || []);
      } else {
        ErrorToast(response?.message || "Could not load audit history");
      }
    } catch (error) {
      console.error(error);
      ErrorToast("Could not load audit history");
    } finally {
      setAuditLoading(false);
    }
  };

  const deleteCoinPackage = () => {
    const id = rowDataToDelete.id;

    let url = `${endPoints.api.DELETE_PACKAGE}${id}`;
    setIsLoading(false);
    deleteData(url)
      .then((response) => {
        setIsLoading(false);
        if (response.status === 200) {
          setShowDeleteModal(false);
          fetchCoinPackage();
          SuccesToast("Deleted Successfully");
        } else {
        }
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const handleSwitchChange = (row, param, value) => {
    const updatedRow = { ...row, [param]: value === 0 ? 1 : 0 };
    updateCoinPackage(updatedRow);
  };

  const updateCoinPackage = (payload) => {
    let url = `${endPoints.api.UPDATE_PACKAGE}`;
    setIsLoading(true);
    addData(url, payload)
      .then((response) => {
        setIsLoading(false);
        if (response.data.status === "success") {
          SuccesToast(response?.data?.message);
          fetchCoinPackage();

          handleClose();
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
        ErrorToast(error);
      });
  };

  // CoinPackage Filter
  const [filterValue, setFilterValue] = useState("");
  // "" = every package. Otherwise a player_type id: a package applies to a
  // player type unless that type sits in its exclude_player, so this answers
  // "which packages does this player type actually get offered?".
  const [playerTypeFilter, setPlayerTypeFilter] = useState("");

  const handleFilterChange = (event) => {
    setFilterValue(event.target.value.toLowerCase());
    setPage(0);
  };

  const handlePlayerTypeFilterChange = (event) => {
    setPlayerTypeFilter(event.target.value);
    setPage(0);
  };

  // Both filters narrow the same list, so they are derived from `rows` together
  // rather than kept in their own state — a cached copy would go stale the next
  // time fetchCoinPackage() replaces the rows behind it.
  const rowsToDisplay = React.useMemo(() => {
    let result = rows;
    if (filterValue) {
      result = result.filter((item) =>
        String(item.package_name || "").toLowerCase().includes(filterValue)
      );
    }
    if (playerTypeFilter !== "") {
      const id = Number(playerTypeFilter);
      result = result.filter((item) => !excludedPlayerIds(item).includes(id));
    }
    return result;
  }, [rows, filterValue, playerTypeFilter]);

  // The player site keys the buy-coins popup off the package's long
  // `packageId`, not its row `id` — pass the whole record, never the id.
  // Returns "" when the record has no packageId so we never hand the admin a
  // ...packageId=undefined link.
  const fetchEmailurl = (pkg) =>
    pkg?.packageId
      ? `${PlayerSiteUrl}/buy-coins?packageId=${pkg.packageId}&segment=N1`
      : "";

  const openCreate = () => {
    setShow(true);
    setEditData({});
    setCreateData(true);
  };

  const openEdit = (row) => {
    setShow(true);
    setCreateData(true);
    setEditData(row);
  };

  const flags = [
    ["dailyBounsEnable", "Daily Bouns"],
    ["isCheckedActive", "Active"],
    ["isSpecial", "Special"],
    ["isFeature", "Featured"],
    ["isPopular", "Popular"],
    ["freeSpin", "Free Spin"],
    ["isBoost", "Coin Boost"],
    ["dailyStreak", "Daily Streak"],
    ["emailPackage", "Email Package"],
    ["isLottery", "Lottery"],
  ];

  return (
    <Box>
      <Toaster />
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <DeleteModel
        showModal={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={deleteCoinPackage}
      />

      <PageHeader
        title="Coin Package"
        crumb="Casino / Coin Package"
        subtitle={
          pending
            ? "Loading packages…"
            : `Purchasable coin bundles · ${rows.length} packages`
        }
        actions={
          <Button
            size="small"
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={openCreate}
          >
            Add New Coin Package
          </Button>
        }
      />

      <SectionCard sx={{ height: "auto" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Enter package name to search…"
            value={filterValue}
            onChange={handleFilterChange}
            sx={{ minWidth: 260, ...fieldSx }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: 18, color: tokens.textDim }} />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 210 }}>
            <Select
              displayEmpty
              value={playerTypeFilter}
              onChange={handlePlayerTypeFilterChange}
              input={<OutlinedInput />}
              sx={{ fontSize: 13 }}
              renderValue={(selected) =>
                selected === "" ? (
                  <span style={{ color: tokens.textDim }}>All player types</span>
                ) : (
                  playerList.find((p) => p.id === Number(selected))?.player_type || selected
                )
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All player types</MenuItem>
              {playerList.map((player) => (
                <MenuItem key={player.id} value={player.id}>
                  {player.player_type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {playerTypeFilter !== "" && (
            <Typography sx={{ fontSize: 12, color: tokens.textDim }}>
              {`Packages offered to this player type · ${rowsToDisplay.length} of ${rows.length}`}
            </Typography>
          )}
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thCell}>Pack ID</th>
                <th style={thCell}>Name</th>
                <th style={{ ...thCell, textAlign: "center" }}>GC</th>
                <th style={{ ...thCell, textAlign: "center" }}>SC</th>
                <th style={{ ...thCell, textAlign: "center" }}>Price</th>
                <th style={{ ...thCell, textAlign: "center" }}>Total Buy</th>
                <th style={{ ...thCell, textAlign: "center" }}>Sort</th>
                <th style={{ ...thCell, textAlign: "center" }}>Unique</th>
                <th style={{ ...thCell, textAlign: "center" }}>Limit</th>
                <th style={{ ...thCell, textAlign: "center" }}>Day</th>
                <th style={{ ...thCell, textAlign: "center" }}>Play</th>
                <th style={{ ...thCell, textAlign: "center" }}>Creative</th>
                <th style={{ ...thCell, textAlign: "center" }}>Active</th>
                <th style={{ ...thCell, textAlign: "center" }}>Special</th>
                <th style={{ ...thCell, textAlign: "center" }}>Popular</th>
                <th style={{ ...thCell, textAlign: "center" }}>Options</th>
              </tr>
            </thead>
            <tbody>
              {rowsToDisplay.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={16} style={{ ...tdCell, textAlign: "center", color: tokens.textDim }}>
                    No coin packages found.
                  </td>
                </tr>
              )}

              {rowsToDisplay
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <tr key={row.id}>
                    <td style={{ ...tdCell, fontFamily: mono, color: tokens.textDim }}>{row.id}</td>
                    <td style={{ ...tdCell, fontWeight: 600 }}>{row.package_name}</td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>{row.goldcoins}</td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>{row.sagacoins}</td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono, color: tokens.accent }}>
                      ${row.purchase_price}
                    </td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>{row.totalBuy}</td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>{row?.sortOrder}</td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>
                      {row.totalCustomerBuy}
                    </td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>{row.max_claim}</td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>{row.days}</td>
                    <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>
                      {`${row?.multplier}x`}
                    </td>
                    <td style={{ ...tdCell, textAlign: "center" }}>
                      <Box
                        component="img"
                        src={`${DefaultImageBaseurl}${row.creative}`}
                        alt={row.package_name}
                        sx={{
                          height: 40,
                          borderRadius: "6px",
                          border: `1px solid ${tokens.border}`,
                          display: "block",
                          mx: "auto",
                        }}
                      />
                    </td>
                    <td style={{ ...tdCell, textAlign: "center" }}>
                      <Switch
                        size="small"
                        checked={row.isactive !== 0}
                        onChange={() => handleSwitchChange(row, "isactive", row.isactive)}
                      />
                    </td>
                    <td style={{ ...tdCell, textAlign: "center" }}>
                      <Switch
                        size="small"
                        checked={row.isSpecial !== 0}
                        onChange={() => handleSwitchChange(row, "isSpecial", row.isSpecial)}
                      />
                    </td>
                    <td style={{ ...tdCell, textAlign: "center" }}>
                      <Switch
                        size="small"
                        checked={row.isPopular !== 0}
                        onChange={() => handleSwitchChange(row, "isPopular", row.isPopular)}
                      />
                    </td>
                    <td style={{ ...tdCell, textAlign: "center" }}>
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.3 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon sx={{ fontSize: 18, color: tokens.textDim }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Audit">
                          <IconButton size="small" onClick={() => openAudit(row)}>
                            <HistoryRoundedIcon sx={{ fontSize: 18, color: tokens.textDim }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(row)}>
                            <DeleteIcon sx={{ fontSize: 18, color: tokens.danger }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
          count={rowsToDisplay.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          sx={{ color: tokens.textDim, borderTop: `1px solid ${tokens.border}` }}
        />
      </SectionCard>

      {/* Create / edit package */}
      <Dialog
        open={show}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogHeader title="Manage Coin Package" onClose={handleClose} />

        <Box sx={{ p: 2.5, maxHeight: "72vh", overflowY: "auto" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
            <Field label="Package Name">
              <TextField
                fullWidth
                size="small"
                placeholder="Package Name"
                value={packageName}
                onChange={(event) => setPackageName(event.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Upload Creative">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Button component="label" size="small" variant="outlined" sx={ghostBtnSx}>
                  Choose file
                  <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                </Button>
                <Typography
                  sx={{ fontSize: 12, color: tokens.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {creativeName || (creative ? "Current creative" : "No file chosen")}
                </Typography>
              </Box>
            </Field>

            <Field label="Gold Coin" span={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Gold Coin"
                value={goldCoin}
                onChange={(event) => setGoldCoin(event.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Sort Order" span={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Sort Order"
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Preview" span={4}>
              {showcreative ? (
                <Box
                  component="img"
                  src={showcreative}
                  alt="Selected creative"
                  sx={{
                    maxWidth: 100,
                    maxHeight: 56,
                    borderRadius: "8px",
                    border: `1px solid ${tokens.border}`,
                  }}
                />
              ) : (
                <Typography sx={{ fontSize: 12, color: tokens.textDim2 || tokens.textDim }}>
                  No creative selected
                </Typography>
              )}
            </Field>

            <Field label="Saga Coin">
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Saga Coin"
                value={sagaCoin}
                onChange={(event) => setSagaCoin(event.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Purchase Price">
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Purchase Price"
                value={purchasePrice}
                onChange={(event) => setPurchasePrice(event.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Daily Transfer Coin">
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Daily Transfer Coin"
                value={maxCliam}
                onChange={(event) => setMaxCliam(event.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Package Exclude">
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  displayEmpty
                  value={excludePackage}
                  onChange={handleChange}
                  MenuProps={MenuProps}
                  sx={{ fontSize: 13 }}
                  renderValue={(selected) =>
                    selected?.length ? (
                      selected
                        .map(
                          (id) => totalPackage?.find((item) => item.id === id)?.package_name
                        )
                        .join(", ")
                    ) : (
                      <span style={{ color: tokens.textDim }}>Select packages to exclude</span>
                    )
                  }
                >
                  {totalPackage.map((item) => {
                    if (editData?.id === item.id) {
                      return null;
                    }
                    return (
                      <MenuItem key={item.id} value={item.id}>
                        <Checkbox size="small" checked={excludePackage.includes(item.id)} />
                        <ListItemText primary={item.package_name} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Field>

            <Field label="Limit">
              <TextField
                select
                fullWidth
                size="small"
                value={limit}
                onChange={(event) => setLimit(event.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="">Select Limit</MenuItem>
                {LIMIT_OPTIONS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            </Field>

            <Field label="Day">
              <TextField
                select
                fullWidth
                size="small"
                value={day}
                onChange={(event) => setDay(event.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="">Select Days</MenuItem>
                {Array.from({ length: 30 }, (_, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {index + 1}
                  </MenuItem>
                ))}
              </TextField>
            </Field>

            <Field label="Play">
              <TextField
                select
                fullWidth
                size="small"
                value={multiplier}
                onChange={(event) => setMultiplier(event.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="">Select Play</MenuItem>
                {Array.from({ length: 10 }, (_, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {index + 1}x
                  </MenuItem>
                ))}
              </TextField>
            </Field>

            <Field label="Daily Bouns Coin">
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Daily Bouns Coin"
                value={dailyBounsAmount}
                onChange={(event) => setDailyBounsAmount(event.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Bouns Days">
              <TextField
                select
                fullWidth
                size="small"
                value={bounsDays}
                onChange={(event) => setBounsDays(event.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="">Select Bouns Days</MenuItem>
                {LIMIT_OPTIONS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            </Field>

            {/* Flags */}
            <Box sx={{ gridColumn: "span 12" }}>
              <Typography
                sx={{
                  color: tokens.textDim,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontWeight: 700,
                  mb: 0.8,
                }}
              >
                Options
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  columnGap: 2,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: "10px",
                  background: tokens.bg2,
                  p: 1.2,
                }}
              >
                {flags.map(([name, label]) => (
                  <FormControlLabel
                    key={name}
                    sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                    control={
                      <Checkbox
                        size="small"
                        name={name}
                        checked={checkboxes[name]}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            </Box>

            {copyUrlVisible && checkboxes.emailPackage && (
              <Box sx={{ gridColumn: "span 12" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1.2,
                    borderRadius: "10px",
                    border: `1px solid ${tokens.border}`,
                    background: tokens.bg2,
                  }}
                >
                  <Typography
                    sx={{ flex: 1, wordBreak: "break-all", fontFamily: mono, fontSize: 12 }}
                  >
                    {packageUrl}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={ghostBtnSx}
                    startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                    onClick={handleCopyUrl}
                  >
                    Copy URL
                  </Button>
                </Box>
              </Box>
            )}

            {checkboxes.isBoost && (
              <Field label="Boost Value (%)">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="Enter boost value"
                  value={boostValue}
                  onChange={(event) => setBoostValue(event.target.value)}
                  sx={fieldSx}
                />
              </Field>
            )}

            {checkboxes.freeSpin && (
              <>
                <Field label="Game">
                  <Autocomplete
                    options={filteredGames}
                    getOptionLabel={(option) => option?.game_name || ""}
                    value={freeSpinGame || null}
                    onChange={(e, newValue) => {
                      setFreeSpinGame(newValue || null);
                      setFreeSpingameid(newValue ? newValue?.game_id : "");
                      setFreeSpinGameName(newValue ? newValue?.game_name : "");
                      setGameSearchTerm(newValue ? newValue.game_name : "");
                    }}
                    onInputChange={(e, newInputValue, reason) => {
                      if (reason === "input") setGameSearchTerm(newInputValue);
                    }}
                    inputValue={gameSearchTerm}
                    loading={loadingGames}
                    noOptionsText={gameSearchTerm.length < 2 ? "Type 2+ chars" : "No games"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search game…"
                        variant="outlined"
                        fullWidth
                        sx={fieldSx}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 18, color: tokens.textDim }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {loadingGames ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(o, v) => o?.game_id === v?.game_id}
                  />
                </Field>

                <Field label="Expiry Date">
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={freeSpinToDate}
                    onChange={(e) => setFreeSpinToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </Field>

                {freeSpinGame && (
                  <>
                    <Field label="Spin Count">
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={spincount}
                        onChange={(event) => setSpincount(event.target.value)}
                        sx={fieldSx}
                      >
                        <MenuItem value="">Select Count</MenuItem>
                        {freeSpinGame?.freeCounts?.map((count) => (
                          <MenuItem key={count} value={count}>
                            {count}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Field>

                    <Field label="Free Spin Value">
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={freeSpinValue}
                        onChange={(event) => setFreeSpinValue(event.target.value)}
                        sx={fieldSx}
                      >
                        <MenuItem value="">Select free Spin</MenuItem>
                        {freeSpinGame?.betSize?.map((betSize) => (
                          <MenuItem key={betSize} value={betSize}>
                            {betSize}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Field>
                  </>
                )}
              </>
            )}

            {checkboxes.isFeature && (
              <Field label="Feature Name">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Feature Name"
                  value={featureName}
                  onChange={(e) => setFeatureName(e.target.value)}
                  sx={fieldSx}
                />
              </Field>
            )}

            <Field label="Exclude Player Type" span={12}>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  displayEmpty
                  value={excludePlayerTypes}
                  onChange={(e) =>
                    setExcludePlayerTypes(
                      (typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                      ).map(Number)
                    )
                  }
                  input={<OutlinedInput />}
                  sx={{ fontSize: 13 }}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return (
                        <span style={{ color: tokens.textDim }}>
                          Select player types to exclude
                        </span>
                      );
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {selected.map((id) => {
                          const p = playerList.find((x) => x.id === id);
                          return <Chip key={id} size="small" label={p ? p.player_type : id} />;
                        })}
                      </Box>
                    );
                  }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } }, sx: { zIndex: 1600 } }}
                >
                  {playerList.map((player) => (
                    <MenuItem key={player.id} value={player.id}>
                      <Checkbox size="small" checked={excludePlayerTypes.includes(player.id)} />
                      <ListItemText primary={player.player_type} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 2.5,
            py: 1.75,
            borderTop: `1px solid ${tokens.border}`,
          }}
        >
          <Button size="small" variant="outlined" sx={ghostBtnSx} onClick={handleClose}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleOk}>
            Apply
          </Button>
        </Box>
      </Dialog>

      {/* Audit history */}
      <Dialog
        open={auditOpen}
        onClose={closeAudit}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogHeader
          title={`Audit History${auditPackage ? ` · ${auditPackage.package_name}` : ""}`}
          onClose={closeAudit}
        />

        <Box sx={{ p: 2.5, maxHeight: "72vh", overflowY: "auto" }}>
          {auditLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!auditLoading && auditRows.length === 0 && (
            <Typography sx={{ fontSize: 13, color: tokens.textDim, textAlign: "center", py: 3 }}>
              No audit records found.
            </Typography>
          )}

          {!auditLoading && auditRows.length > 0 && (
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thCell}>Audit ID</th>
                    <th style={thCell}>Action</th>
                    <th style={thCell}>Audit On</th>
                    <th style={thCell}>Updated By</th>
                    <th style={{ ...thCell, textAlign: "center" }}>Active</th>
                    <th style={{ ...thCell, textAlign: "center" }}>GC</th>
                    <th style={{ ...thCell, textAlign: "center" }}>SC</th>
                    <th style={{ ...thCell, textAlign: "center" }}>Price</th>
                    <th style={{ ...thCell, textAlign: "center" }}>Popular</th>
                    <th style={{ ...thCell, textAlign: "center" }}>Special</th>
                    <th style={thCell}>Updated On</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.map((row) => (
                    <tr key={row.id_audit}>
                      <td style={{ ...tdCell, fontFamily: mono, color: tokens.textDim }}>
                        {row.id_audit}
                      </td>
                      <td style={tdCell}>{row.audit_action}</td>
                      <td style={{ ...tdCell, fontFamily: mono, color: tokens.textDim }}>
                        {fmtAuditDate(row.audit_on)}
                      </td>
                      <td style={{ ...tdCell, fontFamily: mono }}>{row.updated_by ?? "—"}</td>
                      <td style={{ ...tdCell, textAlign: "center" }}>{row.Isactive ? "Yes" : "No"}</td>
                      <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>{row.goldcoins}</td>
                      <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>{row.sagacoins}</td>
                      <td style={{ ...tdCell, textAlign: "center", fontFamily: mono }}>
                        ${row.purchase_price}
                      </td>
                      <td style={{ ...tdCell, textAlign: "center" }}>{row.isPopular ? "Yes" : "No"}</td>
                      <td style={{ ...tdCell, textAlign: "center" }}>{row.isSpecial ? "Yes" : "No"}</td>
                      <td style={{ ...tdCell, fontFamily: mono, color: tokens.textDim }}>
                        {fmtAuditDate(row.updated_on)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default CoinPackage;
